import Header from "@/components/Header"
import Footer from "@/components/Footer"

const policySections = [
  {
    id: "introduction",
    title: "Introduction",
    body: (
      <p className="leading-[28px]">
        iData Tech, Inc (“iData Tech,” “we,” “us” or “our”) provides institutional-grade financial-markets data to
        corporate customers through secure SFTP feeds and application-programming interfaces (“API”). This Privacy
        Policy explains how we collect, use, disclose and protect information when you visit www.idatausa.com (the
        “Site”) or engage our data-delivery services (collectively, the “Services”). It also describes the choices and
        rights available to you.
      </p>
    ),
  },
  {
    id: "scope",
    title: "Scope",
    body: (
      <>
        <p>This Policy covers:</p>
        <ol className="list-decimal list-inside mt-[2px] space-y-1">
          <li>Website visitors – people who browse the public Site.</li>
          <li>
            Prospective and current corporate clients and their authorised users – entities or individuals who request
            trials, receive credentials or otherwise interact with our SFTP/API endpoints.
          </li>
        </ol>
        <p>
          It does not apply to the market-data content we license, which contains no personally identifiable information
          (“PII”).
        </p>
      </>
    ),
  },
  {
    id: "information-we-collect",
    title: "Information We Collect",
    body: (
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-gray-300">
          <thead>
            <tr>
              {["Category", "Examples", "Source", "Purpose"].map((h) => (
                <th key={h} className="p-3 border border-[#000] text-center font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['Contact & Account Data',
               'name, title, business e-mail, phone, billing address, login ID',
               'direct from you (web forms, contracts, support tickets)',
               'account set-up, client service'],
               ['Technical Data',
               'IP address, browser/OS, device identifiers, log files, API call metadata, time stamps, pages visited',
               'automated via cookies, server logs and API gateways',
               'service delivery, intrusion prevention, usage analytics'],
               ['Marketing Data',
               'newsletter opt-ins, event attendance, feedback',
               'direct from you',
               'sending product updates, measuring campaign effectiveness'],
               ['Payment & Transaction Data',
               'Purchase records, invoicing details, partial payment-card data (processed by PCI-compliant provider)',
               'Your organization or payment processor',
               'account set-up, client service, and payment collection.'],
            ].map((arr, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {arr.map((txt, j) => (
                    <td key={j} className="p-3 border border-[#000]">
                      {txt}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: "How We Use Information",
    title: "How We Use Information",
    body: (
      <ol className="list-decimal list-inside mt-[2px] space-y-1">
        <li>To evaluate and fulfill requests for trials or subscriptions.</li>
        <li>To authenticate users and deliver data via SFTP or API</li>
        <li>To monitor system performance, detect fraud or abuse and ensure compliance with our license terms</li>
        <li>To send administrative or marketing communications (you may opt out at any time)</li>
      </ol>
    )
  },
  {
    id: "Legal Bases for Processing (GDPR)",
    title: "Legal Bases for Processing (GDPR)",
    body: (
      <>
        <p>Where the EU/UK General Data Protection Regulation (“GDPR”) applies, we rely on:</p>
        <ol className="list-decimal list-inside mt-[2px] space-y-1">
          <li>Contract performance – providing the Services you request</li>
          <li>Legitimate interests – securing our network and improving our Site</li>
          <li>Consent – optional marketing e-mails (withdraw at any time)</li>
          <li>Legal obligations – record-keeping or responding to lawful requestsGDPR gives data subjects rights to access, rectify, erase and restrict certain processing.</li>
        </ol>
      </>     
    )
  },
  {
    id: "Sharing & Disclosure",
    title: "Sharing & Disclosure",
    body: (
      <>
        <p>We never sell personal information. We share it only:</p>
        <ol className="list-decimal list-inside mt-[2px] space-y-1">
          <li>With our affiliates under confidentiality obligations</li>
          <li>With vetted vendors (ex- cloud hosting, payment processors) who must process data solely for us and protect it appropriately</li>
          <li>With authorities when required to comply with law, enforce agreements or protect rights, safety or property</li>
          <li>In a business transfer (ex- merger or acquisition) with notice to affected users.</li>
        </ol>
      </>     
    )
  },
  {
    id: "Cookies & Similar Technologies",
    title: "Cookies & Similar Technologies",
    body: (
        <p>The Site uses strictly necessary cookies for session management and security.
          We also use first-party analytics cookies to understand aggregate traffic patterns. You can control cookies
          through your browser; disabling them may affect site functionality.</p>   
    )
  },
  {
    id: "Data Security",
    title: "Data Security",
    body: (
      <>
        <p>We implement administrative, technical and physical safeguards aligned with ISO 27001 and NIST standards, including:</p>
        <ol className="list-decimal list-inside mt-[2px] space-y-1">
          <li>Encryption in transit (TLS 1.2+) and at rest (AES-256)</li>
          <li>Role-based access controls and MFA for SFTP/API credentials</li>
          <li>24 × 7 network monitoring, intrusion-detection and vulnerability management</li>
          <li>Periodic penetration testing</li>
        </ol>
      </>     
    )
  },
  {
    id: "International Transfers",
    title: "International Transfers",
    body: (
        <p>If we transfer personal data outside its country of origin (ex- to U.S. data centers), we rely on Standard Contractual Clauses or other approved mechanisms to ensure an adequate level of protection.</p>   
    )
  },
  {
    id: "Data Retention",
    title: "Data Retention",
    body: (
      <>
        <p>We retain personal information for the longer of:</p>
        <p className="pl-8">the duration of the client relationship plus seven (7) years for legal record-keeping, or as necessary to fulfill the purposes described in this Policy.</p>
        <p>Authentication logs are typically stored for 12 months unless required for dispute resolution or security investigations.</p>
      </>     
    )
  },
  {
    id: "Your Privacy Rights",
    title: "Your Privacy Rights",
    body: (
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-gray-300">
          <thead>
            <tr>
              {["Jurisdiction", "Key Rights & How to Exercise Them"].map((h) => (
                <th key={h} className="p-3 border border-[#000] text-center font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['EU/UK GDPR',
               'access, correction, deletion, portability, objection and restriction.e-mail privacy@idata.com or use the web form.',],
               ['California (CCPA/CPRA)',
               'right to know, delete, correct and opt-out of “sharing” for cross-context behavioral advertising. Submit requests via toll-free +1-833-DATA-USA or the web form.'],
            ].map((arr, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {arr.map((txt, j) => (
                    <td key={j} className="p-3 border border-[#000]">
                      {txt}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p>We will verify your identity before fulfilling a request and respond within applicable statutory timeframes.</p>
      </div>
    ),
  },
  {
    id: "Jurisdiction-Specific Notices & Privacy Choices",
    title: "Jurisdiction-Specific Notices & Privacy Choices",
    body: (
      <>
        <p>Below are the additional rights and choices available to residents of California, Nevada, and the European
          Union / United Kingdom. To exercise any of these rights, email <u>support@idata.com</u>. We will verify your
          identity or that of your authorized agent before acting on a request.</p>
        <p className="mt-4">12.1 California Residents (CCPA / CPRA)</p>
        <table className="w-full text-sm border border-gray-300">
          <thead>
            <tr>
              {["Your Right", "What It Means", "How to Exercise"].map((h) => (
                <th key={h} className="p-3 border border-[#000] text-center font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['Right to Know',
               'Request the categories and/or specific pieces of personal information we have collected, used, disclosed, or (if applicable) sold in the past 12 months.',
              'Email support@idata.com. We will verify your request via the email associated with your account. Limit: 2 free reports per 12-month period.'],
              ['Right to Delete',
              'Ask us to delete personal information we collected from you, subject to statutory exceptions (e.g., security, compliance, warranty).',
               'Same as above.'],
               ['Right to Opt-Out of “Sales”',
              'Direct us not to “sell” or “share” your personal information as defined in Cal. Civ. Code 1798.140. We do not sell personal information, but we honor any opt-out request.',
               'Use our “Do Not Sell or Share My Info” link or email us.'],
               ['Right to Non-Discrimination',
              'We will not deny goods/services, charge different prices, or provide a different level of quality if you exercise CCPA rights.',
               ''],
               ['Authorized Agent',
              'You may designate an agent (with signed permission or power of attorney) to submit a request on your behalf.',
               'Agent must supply proof of authorization plus their own ID'],
               ['Shine the Light',
              'Once per year, request a list of categories of personal information disclosed to third parties for their direct-marketing purposes in the preceding calendar year.',
               'Email support@idata.com with subject “Shine the Light Request.”'],
               ['Do Not Track',
              'Our Site currently does not respond to browser DNT signals, but we honor a valid Global Privacy Control (GPC) as an opt-out of sale/share.',
               'Configure your browser or extension to send the GPC signal.'],
            ].map((arr, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {arr.map((txt, j) => (
                    <td key={j} className="p-3 border border-[#000]">
                      {txt}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-4">12.2 Nevada Residents (NRS 603A)</p>
        <ol className="list-decimal list-inside mt-[2px] space-y-1">
          <li>We do not sell “covered information” for monetary consideration.</li>
          <li>You may nonetheless submit a Nevada Opt-Out Request to support@idata.com directing us not to sell your covered information in the future.</li>
        </ol>
        <p className="mt-4">12.3 European Union & United Kingdom Residents (GDPR / UK GDPR)</p>
        <table className="w-full text-sm border border-gray-300">
          <thead>
            <tr>
              {["Your Right", "Brief Description"].map((h) => (
                <th key={h} className="p-3 border border-[#000] text-center font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['Access',
               'Obtain confirmation of whether we process your personal data and, if so, receive a copy.'],
              ['Rectification',
               'Correct inaccurate or incomplete data.'],
              ['Erasure (“Right to be Forgotten”)',
              'Request deletion where no lawful basis exists to retain the data.'],
              ['Restriction',
              'Limit processing while we verify or contest accuracy, or when you object.'],
              ['Portability',
              'Receive your data in a structured, machine-readable format and transmit it to another controller.'],
              ['Object',
              'Object to processing based on legitimate interests or to direct marketing.'],
              ['Withdraw Consent',
              'Withdraw any consent given at any time; this does not affect prior lawful processing.'],
              ['Lodge a Complaint',
              'Contact your local supervisory authority if you believe we are processing your data unlawfully.'],
            ].map((arr, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {arr.map((txt, j) => (
                    <td key={j} className="p-3 border border-[#000]">
                      {txt}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p><i>To exercise any of the above, email support@idata.com. We respond within one month (extendable by two months for complex requests).</i></p>
        <p className="mt-4">12.4 Privacy Choices Form (All Jurisdictions)</p>
        <p>If you prefer to limit sharing of your personal information:</p>
        <ul className="list-decimal list-inside mt-[2px] space-y-1 list-none pl-6">
          <li>Email support@idata.com from your registered address and state “Privacy Choices” in the subject line.</li>
          <li>
            <p>Indicate any or all of the following instructions:</p>
            <ul className="list-none pl-6">
              <li>“Limit disclosure of my personal information to non-affiliated third parties.”</li>
              <li>“Limit the personal information you share with affiliates.”</li>
              <li>“Remove my name from marketing lists shared with affiliates in related lines of business.”</li>
            </ul>
          </li>
          <li>Include your username and registered email.</li>
        </ul>
        <p>Your preferences will become effective within 30 days of our receipt. You need not resend the form if you have already opted out.</p>
        <p>Verification & Record-Keeping – We may keep a log of requests and responses as required by law and to demonstrate compliance but will not use that log for unrelated purposes.</p>
      </>
           
    )
  },
  {
    id: "Marketing Opt Out",
    title: "Marketing Opt Out",
    body: (
        <p>Click “unsubscribe” in any marketing e-mail or contact us to be removed from our lists.
          Transactional messages (ex- service notices) are not subject to opt-out.</p>   
    )
  },
  {
    id: "Third Party Links",
    title: "Third Party Links",
    body: (
        <p>The Site may contain links to third-party sites or services. We are not responsible for their privacy or
          security practices. Please review their policies before providing information.</p>   
    )
  },
  {
    id: "Children’s Privacy",
    title: "Children’s Privacy",
    body: (
        <p>Our Services are directed to business users. We do not knowingly collect information from anyone under 18. If you believe
          a child has provided us personal data, contact us and we will delete it.</p>   
    )
  },
  {
    id: "Changes to This Policy",
    title: "Changes to This Policy",
    body: (
        <p>We may update this Policy from time to time. Material changes will be posted on the
          Site with a new “Effective Date.” Your continued use after an update constitutes acceptance.
        </p>   
    )
  },
  {
    id: "Contact Us",
    title: "Contact Us",
    body: (
      <>
        <p>For questions, privacy requests or complaints, please contact:</p>   
        <p>Privacy Office – iData Tech, Inc</p>   
        <p>XXXXXXXXXXX</p>   
        <p>E-mail: privacy@idata.com</p>   
        <p>Phone: XXXXXXXXX</p>   
        <p>For questions, privacy requests or complaints, please contact:</p>  
      </> 
    )
  }
]

export default function PrivacyPolicyPage() {
  /* smooth scroll helper */
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" })

  return (
    <div className="bg-white text-gray-800" id="top">
      <Header routes={[
        { path: "/privacy-policy", label: "Privacy Policy" },
        { path: "/term-of-use", label: "Term of Use" }]}
        scrollThreshold={440}
      />
      {/* hero */}
      <section className="h-[440px] font-titillium">
        <div className="relative bg-gray-900 text-white h-full flex items-center bg-[url('@/assets/images/privacy-banner.png')] bg-center bg-cover">
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent" />
          <div className="relative z-10 container max-w-7xl mx-auto px-4 container-max-w">
            <h1 className="text-5xl font-bold">Privacy Policy</h1>
          </div>
        </div>
      </section>

      {/* main */}
      <main className="container container-max-w mx-auto py-16">
        {/* table of contents */}
        <ol className="space-y-[30px] mb-[30px]">
          {policySections.map((s, i) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="underline text-[22px] text-[rgba((22,22,22,0.85)]
              font-bold hover:underline hover:text-[#297FFF]">
                {i + 1}. {s.title}
              </a>
            </li>
          ))}
        </ol>

        <p className="text-[22px] text-[rgba(22,22,22,0.85)] font-bold" >Data Tech, Inc Privacy Policy</p>
        <p className="text-[22px] text-[rgba(22,22,22,0.85)] font-bold mb-[80px]">Effective Date: June 24, 2025</p>

        {/* sections */}
        <div className="space-y-16">
          {policySections.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <header className="mb-4">
                <h2 className="text-[40px]">
                  {i + 1}. {s.title}
                </h2>
                <div onClick={scrollToTop} className="text-[22px] text-[#4D4D4D] underline
                cursor-pointer my-[16px] hover:text-[#297FFF]">back to top</div>
              </header>
              <div className="text-[16px] text-[#4d4d4d]">{s.body}</div>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  )
}
